import { forwardRef, useId, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Spinner>. SVG path,
// a11y wiring, and animation ported letter-for-letter from the vendor's
// compiled bundle (@datasirpi/icons' Spinner icon + IconBase) and
// Spinner-V6PRLUSC.css — same "1em" default, new .rc-spinner class name.
//
// Two pre-existing app bugs fixed here rather than reproduced (de-vendor
// audit, 2026-09-17):
// 1. `size="sm"`/`size="lg"` were passed straight through as the raw SVG
//    `width`/`height` attributes, which is an invalid CSS length and
//    silently falls back to unstyled browser default sizing. This maps the
//    three size keywords to real pixel values while still accepting any
//    raw CSS length/number, so every other call site keeps working
//    unchanged.
// 2. Sites that passed `aria-label` instead of `label` got no
//    `role="status"`, since the vendor only ever wired that off `label` —
//    so those spinners were never announced to screen readers. `label` now
//    falls back to `aria-label` from the rest props so either one works.
const SIZE_MAP = { sm: "12px", md: "1em", lg: "24px" } as const;

export interface SpinnerProps extends HTMLAttributes<HTMLSpanElement> {
  size?: keyof typeof SIZE_MAP | (string & {}) | number;
  label?: string;
}

export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(function Spinner(
  { size = "1em", label, className, "aria-label": ariaLabel, ...rest },
  ref
) {
  const titleId = useId();
  const resolvedLabel = label ?? ariaLabel;
  const resolvedSize = typeof size === "string" && size in SIZE_MAP ? SIZE_MAP[size as keyof typeof SIZE_MAP] : size;
  const svgA11y = resolvedLabel
    ? { role: "img" as const, "aria-labelledby": titleId }
    : { "aria-hidden": true as const, focusable: "false" as const };

  return (
    <span
      ref={ref}
      role={resolvedLabel ? "status" : undefined}
      aria-label={resolvedLabel}
      className={cn(className)}
      {...rest}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width={resolvedSize}
        height={resolvedSize}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="rc-spinner"
        {...svgA11y}
      >
        {resolvedLabel ? <title id={titleId}>{resolvedLabel}</title> : null}
        <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83" />
      </svg>
    </span>
  );
});
